import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { createPortal } from "react-dom";

/**
 * Shown when II session expired or protocol access was lost while the UI
 * still looked "logged in" (empty vaults, Unauthorized on writes).
 */
export function SessionExpiredModal() {
	const {
		sessionStatus,
		dismissSessionPrompt,
		login,
		logout,
		isLoggingIn,
		reconnectProtocol,
	} = useAuth();

	const open =
		sessionStatus === "expired" || sessionStatus === "degraded_prompt";

	if (typeof document === "undefined") return null;

	const isExpired = sessionStatus === "expired";

	return createPortal(
		<AnimatePresence>
			{open ? (
				<div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
					<motion.div
						className="absolute inset-0 bg-black/50 backdrop-blur-sm"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={() => dismissSessionPrompt()}
					/>
					<motion.div
						role="dialog"
						aria-modal
						aria-labelledby="session-expired-title"
						className={cn(
							"relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-card",
						)}
						initial={{ opacity: 0, scale: 0.96, y: 8 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.96, y: 8 }}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex items-start gap-3">
							<div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-warning/15 text-warning">
								<ShieldAlert className="size-5" aria-hidden />
							</div>
							<div className="min-w-0">
								<p className="section-eyebrow">Session</p>
								<h2
									id="session-expired-title"
									className="text-lg font-bold tracking-tight"
								>
									{isExpired
										? "Session expired"
										: "Protocol access lost"}
								</h2>
								<p className="mt-2 text-sm text-muted-foreground">
									{isExpired
										? "Your Internet Identity session ended. Log in again to see vaults, balances, and trade."
										: "You still look connected, but the protocol actor is missing or your user role is not active. Reconnect to restore Your vaults — this is not an empty portfolio."}
								</p>
							</div>
						</div>

						<div className="mt-6 flex flex-col gap-2">
							{isExpired ? (
								<Button
									variant="primary"
									className="w-full"
									loading={isLoggingIn}
									onClick={() => void login()}
								>
									Log in again
								</Button>
							) : (
								<>
									<Button
										variant="primary"
										className="w-full"
										onClick={() => void reconnectProtocol()}
									>
										Reconnect protocol
									</Button>
									<Button
										variant="secondary"
										className="w-full"
										loading={isLoggingIn}
										onClick={() => void login()}
									>
										Full re-login
									</Button>
								</>
							)}
							<button
								type="button"
								className="min-h-10 rounded-xl text-sm font-semibold text-muted-foreground hover:text-foreground"
								onClick={() => {
									dismissSessionPrompt();
									if (isExpired) void logout();
								}}
							>
								{isExpired ? "Browse as guest" : "Dismiss for now"}
							</button>
						</div>
					</motion.div>
				</div>
			) : null}
		</AnimatePresence>,
		document.body,
	);
}
